/**
 * useChat — WebSocket connection + local message state for a chat room.
 *
 * The socket ONLY streams live events; history and posting go through REST so
 * moderation runs in one place server-side. Auto-reconnect with backoff so
 * transient network drops (mobile handoff, Wi-Fi swap) heal silently.
 *
 * Events understood: message.new, message.deleted, reaction.updated,
 * pin.updated, presence.join, presence.leave.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { logWarn } from "@/lib/log";

const WS_BASE = (() => {
  const http = process.env.REACT_APP_BACKEND_URL || "";
  return http.replace(/^http/, "ws");
})();

export function useChat(room) {
  const [messages, setMessages] = useState([]);
  const [pinned, setPinned] = useState([]);
  const [presence, setPresence] = useState(0);
  const [status, setStatus] = useState("connecting"); // connecting | open | closed
  const wsRef = useRef(null);
  const retryRef = useRef(0);
  const closedByUsRef = useRef(false);

  /** Merge a new message respecting created_at ordering and dedupe by id. */
  const upsertMessage = useCallback((msg) => {
    setMessages((prev) => {
      if (prev.some((m) => m.id === msg.id)) return prev;
      // Fast path: newer than tail
      if (!prev.length || prev[prev.length - 1].created_at <= msg.created_at) {
        return [...prev, msg];
      }
      const next = [...prev, msg];
      next.sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
      return next;
    });
  }, []);

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await api.get(`/chat/${room}/messages?limit=80`);
      setMessages(data.messages || []);
      setPinned(data.pinned || []);
    } catch (e) {
      logWarn("[chat] history load failed", e?.response?.status);
    }
  }, [room]);

  const handleEvent = useCallback((evt) => {
    if (!evt || !evt.type) return;
    if (evt.type === "message.new" && evt.message) {
      upsertMessage(evt.message);
    } else if (evt.type === "message.deleted") {
      setMessages((prev) => prev.map((m) => (m.id === evt.id ? { ...m, is_deleted: true, text: "", reactions: {} } : m)));
      setPinned((prev) => prev.filter((m) => m.id !== evt.id));
    } else if (evt.type === "reaction.updated") {
      setMessages((prev) => prev.map((m) => (m.id === evt.id ? { ...m, reactions: evt.reactions } : m)));
    } else if (evt.type === "pin.updated") {
      setMessages((prev) => prev.map((m) => (m.id === evt.id ? { ...m, is_pinned: evt.is_pinned } : m)));
      // Refetch pinned strip — easiest way to keep order correct.
      api.get(`/chat/${room}/messages?limit=1`).then((r) => setPinned(r.data.pinned || [])).catch(() => {});
    } else if (evt.type === "presence.join" || evt.type === "presence.leave") {
      if (typeof evt.count === "number") setPresence(evt.count);
    }
  }, [room, upsertMessage]);

  const connect = useCallback(() => {
    closedByUsRef.current = false;
    const url = `${WS_BASE}/api/chat/${room}/ws`;
    setStatus("connecting");
    const ws = new WebSocket(url);
    wsRef.current = ws;
    ws.onopen = () => { setStatus("open"); retryRef.current = 0; };
    ws.onmessage = (ev) => {
      try { handleEvent(JSON.parse(ev.data)); }
      catch { /* server sends JSON only — ignore garbage */ }
    };
    ws.onclose = () => {
      setStatus("closed");
      if (closedByUsRef.current) return;
      // Exponential backoff capped at 15s so we don't hammer the server.
      const delay = Math.min(15000, 800 * Math.pow(2, retryRef.current));
      retryRef.current += 1;
      setTimeout(() => { if (!closedByUsRef.current) connect(); }, delay);
    };
    ws.onerror = () => { /* browser will fire close after — nothing to do here */ };
  }, [room, handleEvent]);

  useEffect(() => {
    loadHistory();
    connect();
    return () => {
      closedByUsRef.current = true;
      try { wsRef.current && wsRef.current.close(); } catch { /* noop */ }
    };
  }, [loadHistory, connect]);

  return { messages, pinned, presence, status, reload: loadHistory };
}
